import { Link, router, usePage } from "@inertiajs/react"
import { LogOut, MailX, Search, Settings } from "lucide-react"
import { useRef, useState } from "react"

import { NotificationDropdown } from "@/components/notification-dropdown"
import { ReviewsDropdown } from "@/components/reviews-dropdown"
import { TopNavClock } from "@/components/top-nav-clock"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"
import { useNavPermissions } from "@/hooks/use-nav-permissions"
import { sessionPath, settingsProfilePath } from "@/routes"

interface AuthUser {
  id: number
  email: string
  name: string
  avatar?: string
}

interface AuthSession {
  id: number
}

export function TopNav() {
  const { auth, email_sending_disabled } = usePage<{
    auth: { user: AuthUser; session: AuthSession }
    email_sending_disabled?: boolean
  }>().props
  const { user, session } = auth
  const getInitials = useInitials()
  const { canSearch } = useNavPermissions()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const menuRef = useRef<HTMLDivElement>(null)

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      router.get("/search", { name: searchQuery.trim() })
    }
  }

  const handleLogout = () => {
    setUserMenuOpen(false)
    router.flushAll()
  }

  return (
    <>
      {email_sending_disabled && (
        <div className="flex items-center justify-center gap-2 bg-amber-500 px-4 py-1.5 text-sm font-medium text-white">
          <MailX className="h-4 w-4" />
          Email sending is currently disabled
        </div>
      )}
      <header className="bg-hpi-blue flex h-12 shrink-0 items-center px-4 text-white">
        {/* Logo */}
        <Link href="/" className="mr-4 flex items-center gap-2" prefetch>
          <img src="/HPI-Logo-White.svg" alt="HawkPI" className="h-7" />
        </Link>

        {/* Search */}
        {canSearch && (
          <div className="relative mx-4 max-w-xs flex-1">
            <Search className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-white/60" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchSubmit}
              className="h-8 w-full rounded border-0 bg-white/15 pr-3 pl-8 text-sm text-white placeholder-white/60 focus:bg-white/25 focus:ring-1 focus:ring-white/30 focus:outline-none"
            />
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right section */}
        <div className="flex items-center gap-4">
          {/* Reviews */}
          <ReviewsDropdown />

          {/* Notifications */}
          <NotificationDropdown />

          {/* Divider */}
          <div className="h-6 w-px bg-white/20" />

          {/* Clock */}
          <TopNavClock />

          {/* Divider */}
          <div className="h-6 w-px bg-white/20" />

          {/* User dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex cursor-pointer items-center gap-2 text-sm text-white/90 hover:text-white"
            >
              <span>{user.email}</span>
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="border-hpi-light-gray absolute top-full right-0 z-50 mt-2 w-64 rounded-lg border bg-white shadow-lg">
                  <div className="border-hpi-pale-gray flex items-center gap-3 border-b px-4 py-3">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user.avatar} alt={user.name} />
                      <AvatarFallback className="bg-hpi-light-blue text-hpi-dark-blue text-sm font-medium">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-hpi-dark-gray truncate text-sm font-medium">
                        {user.name}
                      </div>
                      <div className="text-hpi-med-gray truncate text-xs">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div className="py-1">
                    <Link
                      href={settingsProfilePath()}
                      className="text-hpi-dark-gray hover:bg-hpi-pale-gray flex items-center gap-2 px-4 py-2 text-sm"
                      onClick={() => setUserMenuOpen(false)}
                      prefetch
                    >
                      <Settings className="h-4 w-4" />
                      Settings
                    </Link>
                    <Link
                      method="delete"
                      href={sessionPath({ id: session.id })}
                      as="button"
                      className="text-hpi-dark-gray hover:bg-hpi-pale-gray flex w-full items-center gap-2 px-4 py-2 text-sm"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4" />
                      Log out
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  )
}
